/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { InputProps } from '../../../types/InputComponentProps';
import { Box, Field } from 'chakra-v3';

/**
 * Shared style constants for Select components
 * These styles are reused across SelectInput and MultiSelectInput to maintain consistency
 */
export const selectTriggerStyles = {
  bg: 'brand.tealDark',
  borderColor: 'brand.cta',
  h: '48px',
  px: '16px',
  py: '12px',
  borderRadius: '4px',
  fontSize: '16px',
  lineHeight: '1.4',
  letterSpacing: '0.08px',
  fontFamily: 'body',
  fontWeight: 'normal',
} as const;

export const selectContentStyles = {
  bg: 'brand.tealDark',
  borderWidth: '1px',
  borderColor: 'brand.cta',
  borderRadius: '8px',
  boxShadow: '0px 4px 16px 0px rgba(0,0,0,0.16)',
  overflow: 'hidden',
  p: '0',
} as const;

export const selectItemBaseStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  h: '48px',
  px: '16px',
  py: '12px',
  bg: 'brand.tealDark',
  w: '100%',
} as const;

export function InputContainer(props: InputProps) {
  const { id: _id, label, description, error, isInvalid, isDisabled, isRequired, size: _size, children } = props;
  return (
    <Field.Root invalid={isInvalid} disabled={isDisabled} required={isRequired}>
      {label && (
        <Field.Label
          fontWeight="600"
          color="text.label"
          fontSize="16px"
          lineHeight="1.4"
          letterSpacing="0.08px"
          fontFamily="body"
          display="flex"
          gap="7px"
          alignItems="center"
        >
          {label}
          {isRequired && (
            <Box as="span" color="text.placeholder" fontWeight="600" fontSize="16px">
              *
            </Box>
          )}
        </Field.Label>
      )}
      {description && (
        <Field.HelperText
          color="text.helper"
          fontSize="16px"
          lineHeight="1.4"
          letterSpacing="0.08px"
          fontFamily="body"
          fontWeight="normal"
          mt="0"
        >
          {description}
        </Field.HelperText>
      )}
      <Box width="100%" pt={label || description ? '12px' : '0'}>
        {children}
      </Box>
      {error && (
        <Field.ErrorText color="error.solid" fontSize="16px" lineHeight="1.4" letterSpacing="0.08px" fontFamily="body">
          {error}
        </Field.ErrorText>
      )}
    </Field.Root>
  );
}
